import type { UsageLimitConfig } from "@/types";
import { escapeHtml } from "@/utils";

type UsageLimitEditorOptions = {
  title: string;
  eyebrow: string;
  description: string;
  current?: UsageLimitConfig;
  note?: string;
};

const fields = [
  {
    name: "dailyCreditLimit" as const,
    marker: "日",
    label: "单日费用上限",
    description: "按上海自然日累计最终计费点数",
    unit: "credits"
  },
  {
    name: "monthlyCreditLimit" as const,
    marker: "月",
    label: "单月费用上限",
    description: "按上海自然月累计最终计费点数",
    unit: "credits"
  },
  {
    name: "maxConcurrentRequests" as const,
    marker: "并",
    label: "最大并发请求",
    description: "限制同一时刻正在处理的请求数",
    unit: "个请求"
  }
];

export function showUsageLimitEditor(options: UsageLimitEditorOptions): Promise<UsageLimitConfig | null> {
  return new Promise((resolve) => {
    const dialog = document.createElement("dialog");
    dialog.className = "usage-limit-dialog";
    dialog.innerHTML = `
      <form class="usage-limit-editor">
        <header class="usage-limit-editor-header">
          <div>
            <p class="eyebrow">${escapeHtml(options.eyebrow)}</p>
            <h2>${escapeHtml(options.title)}</h2>
            <p>${escapeHtml(options.description)}</p>
          </div>
          <button type="button" class="icon-button" data-limit-cancel aria-label="关闭">×</button>
        </header>
        <div class="usage-limit-editor-body">
          <div class="usage-limit-option-list">
            ${fields.map((field) => {
              const value = options.current?.[field.name];
              const enabled = value !== null && value !== undefined;
              return `
                <article class="usage-limit-option ${enabled ? "is-enabled" : ""}" data-limit-option>
                  <div class="usage-limit-option-main">
                    <span class="usage-limit-marker">${field.marker}</span>
                    <div>
                      <strong>${field.label}</strong>
                      <small>${field.description}</small>
                    </div>
                    <label class="usage-limit-switch">
                      <input type="checkbox" name="${field.name}-enabled" ${enabled ? "checked" : ""}>
                      <span aria-hidden="true"></span>
                      <em>${enabled ? "已启用" : "不限制"}</em>
                    </label>
                  </div>
                  <label class="usage-limit-value">
                    <span>限制值</span>
                    <div>
                      <input name="${field.name}" type="number" min="1" step="1" inputmode="numeric" value="${value ?? ""}" ${enabled ? "" : "disabled"}>
                      <span>${field.unit}</span>
                    </div>
                  </label>
                </article>
              `;
            }).join("")}
          </div>
          ${options.note ? `<p class="usage-limit-editor-note">${escapeHtml(options.note)}</p>` : ""}
          <p class="usage-limit-editor-error" role="alert" hidden></p>
        </div>
        <footer class="usage-limit-editor-actions">
          <button type="button" class="secondary-button" data-limit-cancel>取消</button>
          <button type="submit" class="primary-button">保存限制</button>
        </footer>
      </form>
    `;

    const form = dialog.querySelector<HTMLFormElement>("form");
    const error = dialog.querySelector<HTMLElement>(".usage-limit-editor-error");
    let submitted = false;

    dialog.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((toggle) => {
      toggle.addEventListener("change", () => {
        const name = toggle.name.replace("-enabled", "");
        const input = dialog.querySelector<HTMLInputElement>(`input[name="${name}"]`);
        const option = toggle.closest<HTMLElement>("[data-limit-option]");
        const state = option?.querySelector<HTMLElement>(".usage-limit-switch em");
        if (input) {
          input.disabled = !toggle.checked;
          if (toggle.checked) input.focus();
        }
        option?.classList.toggle("is-enabled", toggle.checked);
        if (state) state.textContent = toggle.checked ? "已启用" : "不限制";
        if (error) error.hidden = true;
      });
    });

    dialog.querySelectorAll<HTMLElement>("[data-limit-cancel]").forEach((button) => {
      button.addEventListener("click", () => dialog.close("cancel"));
    });

    form?.addEventListener("submit", (event) => {
      event.preventDefault();
      const result = {} as UsageLimitConfig;
      for (const field of fields) {
        const enabled = dialog.querySelector<HTMLInputElement>(`input[name="${field.name}-enabled"]`)?.checked;
        const input = dialog.querySelector<HTMLInputElement>(`input[name="${field.name}"]`);
        if (!enabled) {
          result[field.name] = null;
          continue;
        }
        const value = Number(input?.value);
        if (!Number.isSafeInteger(value) || value <= 0) {
          if (error) {
            error.textContent = `请为“${field.label}”填写大于 0 的整数。`;
            error.hidden = false;
          }
          input?.focus();
          return;
        }
        result[field.name] = value;
      }
      submitted = true;
      dialog.close("save");
      resolve(result);
    });

    dialog.addEventListener("close", () => {
      dialog.remove();
      if (!submitted) resolve(null);
    }, { once: true });
    document.body.append(dialog);
    dialog.showModal();
  });
}
